import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from '../../config';


export default function StudentOnboarding() {
  const navigate = useNavigate();
  const { instituteId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const existingUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [form, setForm] = useState({
    name: existingUser.name || existingUser.email?.split("@")[0] || "",
    penid: existingUser.penId || "",
    studentClass: existingUser.studentClass || existingUser.class || "",
    board: existingUser.board || "cbse",
    favouriteSubject: existingUser.favouriteSubject || "",
    dislikedSubject: existingUser.dislikedSubject || "",
  });

  const [subjectData, setSubjectData] = useState(null);

  const [tests, setTests] = useState(
    existingUser.tests && existingUser.tests.length > 0
      ? existingUser.tests
      : [{ id: Date.now(), type: "past", subject: "", date: "", marks: "" }]
  );

  useEffect(() => {
    // Basic protection to ensure it's a student
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (!user || user.role !== "student") {
        navigate("/dashboard");
      }
    } catch {
      navigate("/");
    }

    // Fetch Subject Data
    fetch("/subject_data.txt")
      .then(res => res.json())
      .then(data => {
        setSubjectData(data);
        if (data.classes && data.classes.length > 0 && !form.studentClass) {
          setForm(prev => ({ ...prev, studentClass: data.classes[0] }));
        }
      })
      .catch(err => console.error("Failed to load subject data", err));
  }, [navigate, form.studentClass]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleTestChange = (id, field, value) => {
    setTests(tests.map(test => 
      test.id === id ? { ...test, [field]: value } : test
    ));
  };

  const addTest = () => {
    setTests([...tests, { id: Date.now(), type: "target", subject: "", date: "", marks: "" }]);
  };

  const removeTest = (id) => {
    setTests(tests.filter(test => test.id !== id));
  };

  const isFormValid = () => {
    if (!form.name.trim()) {
      toast.error("Please enter your full name.");
      return false;
    }
    if (!form.penid.trim()) {
      toast.error("Please enter your Pen ID (the 16-character code found on your device).");
      return false;
    }
    if (!form.studentClass.trim()) {
      toast.error("Please select your class.");
      return false;
    }
    
    // Check if any added tests are incomplete
    const hasIncompleteTest = tests.some(t => 
      (t.subject.trim() || t.date || t.marks) && // If any field is touched
      !(t.subject.trim() && t.date && t.marks)   // But not all are filled
    );

    if (hasIncompleteTest) {
      toast.error("Please complete all fields for the test entries you've added.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    try {
      setIsSubmitting(true);
      // NOTE: Mocking API save for now. Save securely or call actual API in the future.
      if (existingUser.email) {
        // Optimistically update DB
        const res = await fetch(API_BASE_URL + "/api/update-profile", {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${existingUser?.token}`
          },
          body: JSON.stringify({
            email: existingUser.email,
            penid: form.penid,
            name: form.name,
            studentClass: form.studentClass,
            stream: form.stream || "",
            board: form.board,
            favouriteSubject: form.favouriteSubject,
            dislikedSubject: form.dislikedSubject
          }),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to update profile in database");
        }
      }

      // Save to localStorage
      const updatedUser = {
        ...existingUser,
        ...form,
        tests: tests.filter(t => t.subject && t.marks),
        penId: form.penid,
        onboardingComplete: true
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      toast.success("Profile updated successfully!");
      if (instituteId) {
        navigate(`/${instituteId}/lms/pensense-dashboard`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Something went wrong while saving your details");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 relative">
      <div className="w-full max-w-2xl relative">
        <div className="absolute -inset-0.5 bg-gradient-to-tr from-indigo-500 via-sky-500 to-emerald-400 opacity-40 blur-2xl" />
        <div className="relative rounded-3xl border border-slate-800 bg-slate-950/90 p-8 shadow-[0_22px_65px_rgba(15,23,42,0.9)] backdrop-blur">
          
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.22em] text-cyan-400 uppercase">
                Welcome to PenSense
              </p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">
                Complete your profile
              </h2>
            </div>
            <span className="inline-flex min-h-9 items-center justify-center rounded-2xl border border-slate-800 bg-slate-900 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">
              Student Info
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">
                  Full Name
                </label>
                <input
                  name="name"
                  type="text"
                  placeholder="E.g. John Doe"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">
                  Pen ID
                </label>
                <input
                  name="penid"
                  type="text"
                  placeholder="Enter the 16-character ID on your Pen"
                  value={form.penid}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">
                  Class & Stream
                </label>
                <select
                  name="studentClass"
                  value={form.studentClass}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60 appearance-none"
                >
                  <option value="" disabled>Select your class</option>
                  {subjectData?.classes ? (
                    subjectData.classes.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))
                  ) : (
                    <option value={form.studentClass}>{form.studentClass || "Loading..."}</option>
                  )}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">
                  Board
                </label>
                <select
                  name="board"
                  value={form.board}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60 appearance-none"
                >
                  <option value="cbse">CBSE</option>
                  <option value="icse">ICSE</option>
                  <option value="state">State Board</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">
                  Favourite Subject
                </label>
                <input
                  name="favouriteSubject"
                  type="text"
                  placeholder="e.g. Physics"
                  value={form.favouriteSubject}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">
                  Disliked Subject
                </label>
                <input
                  name="dislikedSubject"
                  type="text"
                  placeholder="e.g. Chemistry"
                  value={form.dislikedSubject}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-200">Past & Target Tests</h3>
                <button
                  type="button"
                  onClick={addTest}
                  className="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  + Add Another Test
                </button>
              </div>

              <div className="space-y-3">
                {tests.map((test, index) => (
                  <div key={test.id} className="grid grid-cols-1 md:grid-cols-[1fr,1fr,1fr,1fr,auto] gap-3 items-end p-3 rounded-xl border border-slate-800 bg-slate-900/50">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Test Type</label>
                      <select
                        value={test.type}
                        onChange={(e) => handleTestChange(test.id, "type", e.target.value)}
                        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 outline-none focus:border-indigo-500 appearance-none"
                      >
                        <option value="past">Past Test</option>
                        <option value="target">Target Test</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Subject</label>
                      <input
                        type="text"
                        placeholder="e.g. Math"
                        value={test.subject}
                        onChange={(e) => handleTestChange(test.id, "subject", e.target.value)}
                        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Date</label>
                      <input
                        type="date"
                        value={test.date}
                        onChange={(e) => handleTestChange(test.id, "date", e.target.value)}
                        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 outline-none focus:border-indigo-500"
                        style={{ colorScheme: "dark" }}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Marks / Score</label>
                      <input
                        type="number"
                        placeholder="e.g. 85"
                        value={test.marks}
                        onChange={(e) => handleTestChange(test.id, "marks", e.target.value)}
                        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500"
                      />
                    </div>

                    {tests.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTest(test.id)}
                        className="p-2 mb-px rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors h-[34px]"
                        title="Remove test"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex w-full md:w-auto min-w-[150px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-6 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-indigo-900/40 hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {isSubmitting ? "Saving..." : "Save Profile"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
